<template>
  <div class="i-review-section">
    <div class="header">
      <div>Your skills + our roadmap = <br /><strong>proven impact</strong></div>
    </div>
    <div class="stats">
      <div class="stat">
        <img :src="iconClip" />
        <div>
          <div class="figure">1,100,000+</div>
          reports created
        </div>
      </div>
      <div class="stat">
        <img :src="iconUser" />
        <div>
          <div class="figure">9,500+</div>
          coaching hours
        </div>
      </div>
      <div class="stat">
        <img :src="iconSmile" />
        <div>
          <div class="figure">120,000+</div>
          happy customers
        </div>
      </div>
    </div>
    <div class="reviews">
      <div
        v-for="review in REVIEWS"
        :key="review.name"
        class="review"
      >
        <div class="top">
          <div class="rating"><img :src="ImageStars" /> {{ review.rating }} stars</div>
          <div
            class="comment"
            v-html="review.comment"
          ></div>
        </div>
        <div class="person">
          <div class="name">{{ review.name }}</div>
          <div>{{ review.job }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// ASSETS
import ImageStars from "@/assets/icons/icon-five-slashed-stars.svg"
import iconClip from "@/assets/icons/icon-clipboard-check-purple.svg"
import iconSmile from "@/assets/icons/icon-smile-purple.svg"
import iconUser from "@/assets/icons/icon-user-round-purple.svg"

// CONSTANTS
const REVIEWS = [
  {
    name: "Jessica M.",
    rating: 4.9,
    comment:
      "I’ve taken dozens of career quizzes, <span class='highlighted'><strong>but nothing ever felt this tailored.</strong></span> <br /><br />Jobtest didn’t just give me job titles—it gave me clarity. The personality analysis alone was worth it. I finally understand how I’m wired, and <span class='highlighted'>what work will actually energize me.</span>",
    job: "Senior UX Designer"
  },
  {
    name: "David K.",
    rating: 4.8,
    comment:
      "<strong>I was skeptical—thought it’d be generic.</strong> <br /><br />Instead, <span class='highlighted'>it nailed my strengths,</span> explained why I’ve been feeling stuck, and introduced me to <span class='highlighted'>careers I didn’t even know existed.</span> This report didn’t just make suggestions, it gave me a roadmap. 100% worth it.",
    job: "Product Manager"
  },
  {
    name: "Priya S.",
    rating: 5,
    comment:
      "This was like <span class='highlighted'>therapy meets career coaching</span> in one place. I saw myself clearly for the first time in years—and it didn’t just stop there. <br /><br /><span class='highlighted'>The report</span> translated all of that into <span class='highlighted'>actionable career</span> options, with <span class='highlighted'>salary ranges and next steps.</span>",
    job: "People Operations Specialist"
  },
  {
    name: "Lily T.",
    rating: 4.9,
    comment:
      "Honestly, I expected fluff. What I got was a deep, <span class='highlighted'>personalized breakdown</span> of who I am, what I’m good at, and where I’m likely to thrive. <br /><br /><span class='highlighted'>It helped me reframe my whole job search</span>—and I’ve already <span class='highlighted'>booked two interviews</span> using the resume tips.",
    job: "Marketing Strategist"
  },
  {
    name: "Chris D.",
    rating: 5,
    comment:
      "I thought I needed more degrees to figure my life out—but this report helped me realize <span class='highlighted'>I already had everything I needed to start.</span> <br /><br />It laid out career paths that matched my personality, explained next steps, and made it all feel actually <span class='highlighted'>doable.</span>",
    job: "Entry-Level Product Researcher"
  }
]
</script>

<style scoped lang="scss">
.i-review-section {
  @include flex(column, center, center, $gap: 35px);

  background: linear-gradient(180deg, #fff 0%, #eae7ff 100%);
  padding: 50px 15px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 15px;
    gap: 50px;
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;
    }
  }

  .stats {
    @include flex(column, center, center, wrap);

    width: 100%;

    @media screen and (min-width: $breakpoint-sm) {
      @include flex(row, center, stretch, wrap, $gap: 20px);

      width: unset;
      padding: 24px;
      border-top: 1px solid $zumthor100;
      border-bottom: 1px solid $zumthor100;
    }

    @media screen and (min-width: $breakpoint-md) {
      gap: 90px;
    }

    .stat {
      @include flex(row, center, stretch, $gap: 10px);
      @include text-sm;

      width: 100%;
      padding-bottom: 16px;

      @media screen and (min-width: $breakpoint-sm) {
        width: unset;
        padding-bottom: unset;
      }

      &:not(:last-child) {
        border-bottom: 1px solid $zumthor100;
        margin-bottom: 16px;

        @media screen and (min-width: $breakpoint-sm) {
          border-bottom: unset;
          margin-bottom: unset;
        }
      }

      > div {
        @include flex(column, center, center);

        .figure {
          @include text-xl;

          font-weight: 700;
        }
      }
    }
  }

  .reviews {
    @include flex(row, center, stretch, wrap, $gap: 24px);

    max-width: 1410px;

    .review {
      @include flex(column, space-between, stretch, $gap: 24px);
      @include border-radius-md;
      @include text-sm;

      background-color: $white;
      padding: 24px;
      width: 100%;
      max-width: 420px;
      box-shadow: 0 14px 18px 0 #122b6905;

      .top {
        @include flex(column, start, stretch, $gap: 24px);

        .rating {
          > img {
            vertical-align: sub;
            width: 90px;

            @media screen and (min-width: $breakpoint-md) {
              width: unset;
            }
          }
        }
      }

      .person {
        .name {
          font-weight: 700;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.i-review-section {
  .highlighted {
    background-color: #e2f7f0;
  }
}
</style>
